import { NamespaceInterface } from '@vendasta/embeddings';

import { Namespace } from './types';

/** True when two namespaces refer to the same entity (by id within a kind, or both admin/global). */
export function namespacesEqual(a: NamespaceInterface, b: NamespaceInterface): boolean {
  if (a.accountGroupNamespace?.accountGroupId && b.accountGroupNamespace?.accountGroupId) {
    return a.accountGroupNamespace.accountGroupId === b.accountGroupNamespace.accountGroupId;
  }
  if (a.partnerNamespace?.partnerId && b.partnerNamespace?.partnerId) {
    return a.partnerNamespace.partnerId === b.partnerNamespace.partnerId;
  }
  if (a.adminNamespace && b.adminNamespace) return true;
  return false;
}

/**
 * Stable membership key for a KB. Knowledge bases are unique by namespace + id — not by name, and not
 * by id alone (ids are namespace-scoped, so a granted KB can collide with an owned one on either). All
 * KB↔source membership matching keys off this instead of the display name.
 */
export function kbIdentityKey(namespace: NamespaceInterface | undefined, id: string | undefined): string {
  const target = namespaceInterfaceToTarget(namespace);
  const nsKey = target ? `${target.type}:${target.id}` : '';
  return `${nsKey}/${id ?? ''}`;
}

/**
 * Serialize a namespace to a compact `type:id` string for carrying a KB's owning namespace through the detail-page
 * route as a query param (survives refresh/deep-link, unlike router state). Pairs with {@link namespaceFromRouteParam}.
 * Returns null when the namespace is absent or unrecognized.
 */
export function namespaceToRouteParam(ns: NamespaceInterface | undefined): string | null {
  if (!ns) return null;
  if (ns.accountGroupNamespace?.accountGroupId) return `AccountGroup:${ns.accountGroupNamespace.accountGroupId}`;
  if (ns.partnerNamespace?.partnerId) return `Partner:${ns.partnerNamespace.partnerId}`;
  // The admin/global namespace has no id — it's a single global scope, so encode it as the bare type.
  if (ns.adminNamespace) return 'Global';
  return null;
}

/** Parse a route param (see {@link namespaceToRouteParam}) back into a NamespaceInterface; undefined if unparseable. */
export function namespaceFromRouteParam(param: string | null | undefined): NamespaceInterface | undefined {
  if (!param) return undefined;
  if (param === 'Global') return { adminNamespace: {} };
  const sep = param.indexOf(':');
  if (sep < 0) return undefined;
  const type = param.slice(0, sep);
  const id = param.slice(sep + 1);
  if (!id) return undefined;
  switch (type) {
    case 'AccountGroup':
      return { accountGroupNamespace: { accountGroupId: id } };
    case 'Partner':
      return { partnerNamespace: { partnerId: id } };
    default:
      return undefined;
  }
}

export function namespaceInterfaceToTarget(ns: NamespaceInterface | undefined): Namespace | null {
  if (!ns) return null;
  if (ns.accountGroupNamespace?.accountGroupId) {
    return { id: ns.accountGroupNamespace.accountGroupId, type: 'AccountGroup' };
  }
  if (ns.partnerNamespace?.partnerId) {
    return { id: ns.partnerNamespace.partnerId, type: 'Partner' };
  }
  if (ns.adminNamespace) {
    return { id: 'VMF', type: 'Global' };
  }
  return null;
}
